import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Super Admin bulk "Mark as Paid" toolbar for Travel / Expense lists.
 * Eligible rows: status === "approved" and has an approval_id.
 * Props:
 *   rows: current page items
 *   kind: "travel" | "expense" (for testids + wording)
 *   selected: Set of approval_ids
 *   setSelected: setter
 *   onDone: called after a successful bulk action (parent reloads)
 */
export default function BulkPaidBar({ rows = [], kind, selected, setSelected, onDone }) {
  const eligible = rows.filter((r) => r.status === "approved" && r.approval_id);
  if (eligible.length === 0) return null;

  const toggleAll = () => {
    if (selected.size === eligible.length) setSelected(new Set());
    else setSelected(new Set(eligible.map((r) => r.approval_id)));
  };

  const markPaid = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Mark ${ids.length} approved claim(s) as Paid?`)) return;
    try {
      const r = await api.post("/approvals/mark-paid-bulk", { ids });
      toast.success(`Marked ${r.data.paid} paid${r.data.skipped?.length ? `, ${r.data.skipped.length} skipped` : ""}`);
      setSelected(new Set());
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Card className="p-3 mb-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`${kind}-bulk-paid-toolbar`}>
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[#fd7e14]"
          checked={selected.size === eligible.length && eligible.length > 0}
          onChange={toggleAll}
          data-testid={`${kind}-bulk-paid-select-all`}
        />
        <span>
          Select all approved on this page ({eligible.length})
          {selected.size > 0 && <span className="opacity-70 ml-2">({selected.size} selected)</span>}
        </span>
      </label>
      <Button className="btn-brand" disabled={selected.size === 0} onClick={markPaid} data-testid={`${kind}-bulk-mark-paid-btn`}>
        Mark as Paid ({selected.size})
      </Button>
    </Card>
  );
}
